import {
  FIELD,
  FLOW,
  PROCESS_BUILDER_FLOW,
  LAYOUT,
  VALIDATION_RULE,
  REPORT,
  APEX_CLASS,
  APEX_TRIGGER,
  APPROVAL_PROCESS,
  EMAIL_TEMPLATE,
  RECORD_TYPE,
  WORKFLOW_RULE,
} from '@server/parser';
import { RelatedListProperties } from '@server/documentation';
import { ReactNode } from 'react';
import { NameProperties } from './ParserTypes';

//sweep element types
export const SWEEP_AUTOMATION = 'SWEEP_AUTOMATION';
export const SWEEP_ALERT = 'SWEEP_ALERT';
export const SWEEP_ASSIGNMENT = 'SWEEP_ASSIGNMENT';
export const SWEEP_SCHEDULED_ASSIGNMENT = 'SWEEP_SCHEDULED_ASSIGNMENT';
export const SWEEP_DEDUPE = 'SWEEP_DEDUPE';
export const SWEEP_MATCHING = 'SWEEP_MATCHING';
export const ROLLUP = 'ROLLUP';

// Base interface with common properties shared by all configuration items
export interface ConfigurationItemBase {
  id: string;
  name: string;
  label: string;
  type: string;
  parentType: string;
  metadataId: string;
  objectApiNames: string[];

  description?: string;
  helpText?: string;
  referencedObjects?: string[];
  relatedObjects?: string[];
  link?: string;
  updatedAt?: string;
  createdAt?: string;
  updatedBy?: string;
  createdBy?: string;
  isActive?: boolean;
  namespace?: string;

  _oldId?: string; // Client only, temporary fix for tags until BE is migrated to metadataId

  stagesNames?: string[];
  usedByRecordTypes?: string[];
  usedByLeadingFieldPicklist?: string[];

  match?: string; // For search result matches
}

export interface ConfigurationItemField extends ConfigurationItemBase {
  parentType: typeof FIELD;
  fieldType: string;
  isMandatory?: boolean;
  usage?: number;
  isRollup?: boolean;
  formula?: string;
}

export interface ConfigurationItemLayout extends ConfigurationItemBase {
  parentType: typeof LAYOUT;
  buttons?: NameProperties[];
  relatedLists?: RelatedListProperties[];
}

export interface ConfigurationItemFlow extends ConfigurationItemBase {
  parentType: typeof FLOW | typeof PROCESS_BUILDER_FLOW;
  flowType?: string;
  flowDescription?: string;
  processType?: string;
}

export interface ConfigurationItemValidationRule extends ConfigurationItemBase {
  parentType: typeof VALIDATION_RULE;
  errorMessage?: string;
  formula?: string;
}

export interface ConfigurationItemApexTrigger extends ConfigurationItemBase {
  parentType: typeof APEX_TRIGGER;
}

export interface ConfigurationItemApexClass extends ConfigurationItemBase {
  parentType: typeof APEX_CLASS;
}

export interface ConfigurationItemApprovalProcess extends ConfigurationItemBase {
  parentType: typeof APPROVAL_PROCESS;
}

export interface ConfigurationItemWorkflowRule extends ConfigurationItemBase {
  parentType: typeof WORKFLOW_RULE;
}

export interface ConfigurationItemRecordType extends ConfigurationItemBase {
  parentType: typeof RECORD_TYPE;
}

export interface ConfigurationItemReport extends ConfigurationItemBase {
  parentType: typeof REPORT;
}

export interface ConfigurationItemEmailTemplate extends ConfigurationItemBase {
  parentType: typeof EMAIL_TEMPLATE;
}

export interface ConfigurationItemRollup extends ConfigurationItemBase {
  parentType: typeof ROLLUP;
  fieldType: string;
  isMandatory?: boolean;
  usage?: number;
  isRollup?: boolean;
  formula?: string;
}

interface ConfigurationItemBaseSweepElement extends ConfigurationItemBase {
  actions?: string[];
  trigger?: string;
  actionsToRender?: ReactNode[];
}

// Sweep element types
export interface ConfigurationItemSweepAutomation extends ConfigurationItemBaseSweepElement {
  parentType: typeof SWEEP_AUTOMATION;
}

export interface ConfigurationItemSweepAlert extends ConfigurationItemBaseSweepElement {
  parentType: typeof SWEEP_ALERT;
}

export interface ConfigurationItemSweepAssignment extends ConfigurationItemBaseSweepElement {
  parentType: typeof SWEEP_ASSIGNMENT;
}

export interface ConfigurationItemSweepScheduledAssignment extends ConfigurationItemBaseSweepElement {
  parentType: typeof SWEEP_SCHEDULED_ASSIGNMENT;
}

export interface ConfigurationItemSweepDedupe extends ConfigurationItemBaseSweepElement {
  parentType: typeof SWEEP_DEDUPE;
}

export interface ConfigurationItemSweepMatching extends ConfigurationItemBaseSweepElement {
  parentType: typeof SWEEP_MATCHING;
}

// Generic fallback for unclassified or other types
export interface ConfigurationItemGeneric extends ConfigurationItemBase {
  parentType: string;
}

export type ConfigurationItem =
  | ConfigurationItemField
  | ConfigurationItemLayout
  | ConfigurationItemFlow
  | ConfigurationItemValidationRule
  | ConfigurationItemApexTrigger
  | ConfigurationItemApexClass
  | ConfigurationItemApprovalProcess
  | ConfigurationItemWorkflowRule
  | ConfigurationItemRecordType
  | ConfigurationItemReport
  | ConfigurationItemEmailTemplate
  | ConfigurationItemRollup
  | ConfigurationItemSweepAutomation
  | ConfigurationItemSweepAlert
  | ConfigurationItemSweepAssignment
  | ConfigurationItemSweepScheduledAssignment
  | ConfigurationItemSweepDedupe
  | ConfigurationItemSweepMatching
  | ConfigurationItemGeneric;

// Type guard functions for runtime type narrowing
export function isFieldConfigurationItem(item: ConfigurationItem): item is ConfigurationItemField {
  return item.parentType === FIELD;
}

export function isFlowConfigurationItem(item: ConfigurationItem): item is ConfigurationItemFlow {
  return item.parentType === FLOW || item.parentType === PROCESS_BUILDER_FLOW;
}

export function isRollupConfigurationItem(
  item: ConfigurationItem,
): item is ConfigurationItemRollup {
  return item.parentType === ROLLUP;
}

export function isSweepElementConfigurationItem(
  item: ConfigurationItem,
): item is ConfigurationItemBaseSweepElement {
  return (
    item.parentType === SWEEP_AUTOMATION ||
    item.parentType === SWEEP_ALERT ||
    item.parentType === SWEEP_ASSIGNMENT ||
    item.parentType === SWEEP_SCHEDULED_ASSIGNMENT ||
    item.parentType === SWEEP_DEDUPE ||
    item.parentType === SWEEP_MATCHING
  );
}

export function isValidationRuleConfigurationItem(
  item: ConfigurationItem,
): item is ConfigurationItemValidationRule {
  return item.parentType === VALIDATION_RULE;
}

// Type guard to check if item has field-like properties (Field or Rollup)
export function hasFieldProperties(
  item: ConfigurationItem,
): item is ConfigurationItemField | ConfigurationItemRollup {
  return isFieldConfigurationItem(item) || isRollupConfigurationItem(item);
}
